import VerifiedSenders from "./verified-senders";
import User from "./user";
import SystemMsg from "./system-msg";
import Storage from "./storage";
import Record from "./record";
import Parameter from "./parameter";
import Module from "./module";
import EmailSettings from "./email-settings";
import DocumentsCache from "./documents-cache";
import Collection from "./collection";
import Application from "./application";
import Api from "./api";
import IUserData from "../interface/user-data";
import IUserApplication from "../interface/user-application";
import IStorageLoginData from "../interface/storage-login-data";
import IModule from "../interface/module";
import IApiLoginData from "../interface/api/api-login-data";
import IApiLoginApplication from "../interface/api/api-login-application";
import Global from "../global";
import StorageItem from "../enum/storage-item";
import ErrorCode from "../enum/error-code";

class AuthInstance {

    private Instances: string[] = [];

    /* Public Methods */

    public async login(user: string, password: string, instanceName: string, appId: string = ''): Promise<IUserData> {
        if (user && password && instanceName) {
            if (!this.Instances.length) {
                await this.getInstances();
            }
            const loginApps: IApiLoginApplication[] = await Api.login({
                user: user.toLowerCase(),
                password,
                instanceName
            });
            const applications: IUserApplication[] = this.mapApplications(loginApps);
            appId = appId || this.selectDefaultAppId(user, applications);
            if (appId) {
                User.setUser(user, instanceName, applications);
                const data: IUserData = await this.use(appId);
                User.rememberUser(user, password, instanceName);
                return data;
            } else {
                throw ErrorCode.NoApplications;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async use(appId: string): Promise<IUserData> {
        const application: IUserApplication | null = User.getApplication(appId);
        if (application) {
            const loginData: IApiLoginData = await Api.use(application.id);
            DocumentsCache.clear();
            User.setApplication(appId, loginData);
            Application.setApplication(application);
            await Parameter.init();
            await this.checkDefaultData();
            await VerifiedSenders.checkExpired();
            Collection.clear();
            Record.clear();
            return User.getData();
        } else {
            throw ErrorCode.NoApplication;
        }
    }

    public async register(username: string, password: string, instance: string): Promise<IUserData> {
        const appId: string = `app_${(new Date()).getTime()}`;
        if (!this.Instances.length) {
            await this.getInstances();
        }
        const name: string = username.toLowerCase();
        const appDomain: string = await Api.createUserApplication(name, appId, password, instance);
        const result: IUserData = await this.login(name, password, instance, appDomain);
        await Application.rename(name); // todo - should be moved to api level
        const module: IModule = Module.getDefault(); // todo - should be moved to api level
        module.name = name; // todo - should be moved to api level
        await Module.update(module); // todo - should be moved to api level
        await Application.setPublicEnabled(true);
        if (this.getDefaultInstance() === Global.PROD_INSTANCE_NAME) {
            await SystemMsg.emailRegistration(instance);
        }
        return result;
    }

    public async autologin(): Promise<IUserData> {
        const last: IStorageLoginData | null = Storage.getItem(StorageItem.LatestLogin);
        if (last && last.instanceName) {
            return await this.login(last.user, last.password, last.instanceName);
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    public async getInstances(): Promise<string[]> {
        if (!this.Instances.length) {
            // TODO - Remove this - Instances hack start
            this.Instances = ['main'];
            if (document.location.host.includes('framelink.co')) {
                this.Instances = ['main'];
            } else {
                this.Instances = ['preprod', 'main'];
            }
            // TODO - Remove this - Instances hack end
        }
        return this.Instances;
    }

    public async logout(): Promise<boolean> {
        try {
            if (this.isAuthorized()) {
                await Api.logout();
                Application.logout();
                User.logout();
                await Parameter.clear();
            }
            return this.isAuthorized();
        } catch (e) {
            throw ErrorCode.ApiInternal;
        }
    }

    public getDefaultInstance(): string {
        return Storage.getItem(StorageItem.LatestInstance) || '';
    }

    public isAuthorized(): boolean {
        return User.isAuthorized() && Application.isAuthorized();
    }

    /* Private Methods */

    public clearInstances(): void {
        this.Instances = [];
    }

    private selectDefaultAppId(username: string, applications: IUserApplication[]): string {
        if (applications.length) {
            const latestApp: string = this.getLatestAppId(username, applications);
            if (latestApp) {
                return latestApp;
            } else {
                const owner: IUserApplication | undefined = applications.find((application: IUserApplication) => application.isOwner);
                if (owner) {
                    return owner.id;
                } else {
                    return applications[0].id;
                }
            }
        } else {
            return '';
        }
    }

    private getLatestAppId(username: string, applications: IUserApplication[]): string {
        const latestApp: string | null = Storage.getLatestApplication(username);
        if (latestApp) {
            const application: IUserApplication | null = applications.find((app: IUserApplication) => {
                return app.id === latestApp;
            }) || null;
            if (application) {
                return application.id;
            }
        }
        return '';
    }

    private mapApplications(applications: IApiLoginApplication[]): IUserApplication[] {
        return applications.map((app: IApiLoginApplication) => {
            return {
                id: app.domain,
                name: app.description,
                isOwner: app.isOwner,
                logo: app.logo || '',
                isCurrent: false
            };
        });
    }

    private async checkDefaultData(): Promise<void> {
        if (!EmailSettings.btnGetAll().length) {
            await EmailSettings.btnCreateDefault(); // TODO - Delete this shit
        }
        if (!Module.getAll().length) {
            await Module.create({
                name: Application.getName() as string,
                icon: Global.DEFAULT_MODULE_ICON,
                layout: []
            });
        }
    }

}

const Auth = new AuthInstance();

export default Auth;